import { useEffect, useRef, useState } from 'react'
import './Projects.css'
import ProjectImage from './ProjectImage'
import projects from './project-data.json'
import fitcheckImg from './Project/fitcheck.webp'
import teachmeImg from './Project/teachme.webp'
import didYouRsvpImg from './Project/didyoursvp.webp'
import conferenceGoImg from './Project/conference-go.webp'

const IMAGES = {
    fitcheck: fitcheckImg,
    teachme: teachmeImg,
    didyoursvp: didYouRsvpImg,
    conferencego: conferenceGoImg,
}

const Arrow = ({ direction }) => (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <path
            d={direction === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

// One project at a time, following the W3C ARIA "carousel" pattern:
// labelled region + slides, real Prev/Next buttons, labelled project picker
// (instead of tiny dots), numbered position, swipe and arrow keys, no auto-rotation.
const Projects = () => {
    const [index, setIndex] = useState(0)
    const [direction, setDirection] = useState(1)
    const swipe = useRef(null)
    const pickerRef = useRef(null)
    const count = projects.length
    const project = projects[index]

    // Keep the selected project's pill visible in the (scrollable) picker
    useEffect(() => {
        const current = pickerRef.current && pickerRef.current.querySelector('.is-current')
        if (current) current.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
    }, [index])

    const goTo = (next) => {
        const wrapped = (next + count) % count
        setDirection(next > index ? 1 : -1)
        setIndex(wrapped)
    }

    const onKeyDown = (event) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            goTo(index + 1)
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault()
            goTo(index - 1)
        }
    }

    // Horizontal swipe on touch screens (vertical drags still scroll)
    const onPointerDown = (event) => {
        if (event.pointerType === 'mouse') return
        swipe.current = { x: event.clientX, y: event.clientY }
    }
    const onPointerUp = (event) => {
        const start = swipe.current
        swipe.current = null
        if (!start) return
        const dx = event.clientX - start.x
        const dy = event.clientY - start.y
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) goTo(index + (dx < 0 ? 1 : -1))
    }

    return (
        <section
            className="carousel"
            aria-roledescription="carousel"
            aria-label="Software projects"
            onKeyDown={onKeyDown}
        >
            <div className="carousel__bar">
                <button type="button" className="carousel__btn" onClick={() => goTo(index - 1)} aria-label="Previous project">
                    <Arrow direction="prev" />
                </button>
                <div className="carousel__picker" ref={pickerRef}>
                    {projects.map((p, i) => (
                        <button
                            key={p.id}
                            type="button"
                            className={`carousel__pick${i === index ? ' is-current' : ''}`}
                            aria-current={i === index ? 'true' : undefined}
                            onClick={() => goTo(i)}
                        >
                            {p.title}
                        </button>
                    ))}
                </div>
                <button type="button" className="carousel__btn" onClick={() => goTo(index + 1)} aria-label="Next project">
                    <Arrow direction="next" />
                </button>
            </div>

            <div
                className="carousel__viewport"
                aria-live="polite"
                onPointerDown={onPointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={() => (swipe.current = null)}
            >
                <article
                    key={project.id}
                    className={`slide slide--${direction > 0 ? 'next' : 'prev'}`}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`${index + 1} of ${count}: ${project.title}`}
                >
                    <div className="slide__media">
                        <ProjectImage src={IMAGES[project.id]} alt={`${project.title} screenshot`} />
                        <p className="slide__count" aria-hidden="true">
                            <span>{String(index + 1).padStart(2, '0')}</span> / {String(count).padStart(2, '0')}
                        </p>
                    </div>
                    <div className="slide__body">
                        <h2 className="slide__title">{project.title}</h2>
                        <p className="slide__subtitle">{project.subtitle}</p>
                        <ul className="chips" aria-label="Tools used">
                            {project.tools.map((tool) => (
                                <li key={tool}>{tool}</li>
                            ))}
                        </ul>
                        <ul className="slide__highlights">
                            {project.highlights.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                        <div className="slide__links">
                            {project.links.map((link) => (
                                <a key={link.href} className="link-btn" href={link.href} target="_blank" rel="noreferrer">
                                    {link.label}
                                    <span aria-hidden="true"> ↗</span>
                                </a>
                            ))}
                        </div>
                    </div>
                </article>
            </div>
        </section>
    )
}

export default Projects
